// src/store/index.js
import Vuex from 'vuex';
import Cookies from 'js-cookie'; // 您的现有导入

export default new Vuex.Store({
  state: {
    // --- 现有串口相关状态 ---
    serialPort: null,
    writer: null,
    reader: null,
    serialData: [],
    deviceInfo: null, // 用于存储设备信息
    isSerialConnected: false, // 将 isConnected 更名为 isSerialConnected
    isPortOpen: false, // 串口是否打开
    pendingRequests: {},

    // --- 新增蓝牙相关状态 ---
    // 注意：这里的命名与上面LogNavComponent.vue中保持一致，避免混淆
    gattServer: null,       // 存储 BluetoothRemoteGATTServer 对象
    isBleConnected: false,  // 蓝牙连接状态 (true 表示已连接)
    bleDeviceInfo: null,    // 存储蓝牙设备信息 (例如版本号)
  },
  mutations: {
    // --- 现有串口相关 Mutations (修改 isConnected -> isSerialConnected) ---
    SET_SERIAL_PORT(state, port) {
      state.serialPort = port;
      state.isSerialConnected = true; // 修改这里
    },
    SET_WRITER(state, writer) {
      if (state.writer) {
        state.writer.releaseLock();
      }
      state.writer = writer;
    },
    SET_READER(state, reader) {
      if (state.reader) {
        state.reader.releaseLock();
      }
      state.reader = reader;
    },
    ADD_SERIAL_DATA(state, data) {
      state.serialData.push(data);
    },
    SET_DEVICE_INFO(state, deviceInfo) {
      state.deviceInfo = deviceInfo;
    },
    CLEAR_SERIAL_DATA(state) {
      state.serialData = [];
    },
    async DISCONNECT_SERIAL(state) {
      try {
        if (state.writer) {
          await state.writer.close();
          state.writer.releaseLock();
        }
        if (state.reader) {
          await state.reader.cancel();
          state.reader.releaseLock();
        }
        if (state.serialPort) {
          await state.serialPort.close();
        }
      } catch (error) {
        console.error('关闭串口时出错:', error);
      } finally {
        state.serialPort = null;
        state.writer = null;
        state.reader = null;
        state.isSerialConnected = false; // 修改这里
        state.isPortOpen = false;
        state.deviceInfo = null; // 断开连接时清空设备信息
      }
    },
    SET_IS_PORT_OPEN(state, isOpen) {
      state.isPortOpen = isOpen;
    },
    ADD_PENDING_REQUEST(state, { id, resolve, reject }) {
      state.pendingRequests[id] = { resolve, reject };
    },
    REMOVE_PENDING_REQUEST(state, id) {
      delete state.pendingRequests[id];
    },

    // --- 新增蓝牙相关 Mutations ---
    SET_GATT_SERVER(state, server) {
      state.gattServer = server;
      state.isBleConnected = !!server; // 更新蓝牙连接状态
      if (server) {
        console.log('Bluetooth GATT Server connected:', server);
        // 监听设备断开连接事件
        server.device.addEventListener('gattserverdisconnected', () => {
          state.gattServer = null;
          state.isBleConnected = false;
          state.bleDeviceInfo = null; // 蓝牙断开时清空蓝牙设备信息
          console.log('蓝牙设备已断开连接。');
        });
      } else {
        console.log('Bluetooth GATT Server disconnected.');
      }
    },
    SET_BLE_DEVICE_INFO(state, info) {
        state.bleDeviceInfo = info;
    },
  },
  actions: {
    // --- 现有串口相关 Actions ---
    async connectSerial({ commit, state, dispatch }) {
      try {
        if (state.isPortOpen) {
          alert("已连接！请勿重复连接！");
          return;
        }
        const port = await navigator.serial.requestPort({});
        if (!port) {
          throw new Error('用户取消了串口选择');
        }
        await port.open({ baudRate: 921600 }); // 确保波特率匹配您的设备

        commit('SET_SERIAL_PORT', port);
        const writer = port.writable.getWriter();
        commit('SET_WRITER', writer);
        const reader = port.readable.getReader();
        commit('SET_READER', reader);

        commit('SET_IS_PORT_OPEN', true);

        // 发送设备信息请求
        const m = {
          method: "get_device_info"
        };
        dispatch('sendSerialData', m); // 注意：这里调用的是 sendSerialData
        dispatch('readSerialData'); // 开始读取数据
      } catch (error) {
        if (error.name === 'AbortError') {
          alert("请选择一个串口设备以继续。");
        } else {
          alert("串口打开失败，请刷新后再试！");
          console.error('串口连接失败:', error);
        }
        commit('DISCONNECT_SERIAL');
      }
    },
    async readSerialData({ commit, state, dispatch }) {
      const { serialPort } = state;
      if (!serialPort) return;

      // 确保在每次调用 readSerialData 时都获取一个新的 reader，并释放旧的
      if (state.reader) {
        state.reader.releaseLock();
      }
      const reader = serialPort.readable.getReader();
      commit('SET_READER', reader);

      const readLoop = async () => {
        let buffer = '';

        // 确保使用 state.isSerialConnected 来控制循环
        while (state.isSerialConnected) { // 修改这里
          try {
            const { value, done } = await reader.read();
            if (done) {
              break;
            }
            if (value) {
              const textDecoder = new TextDecoder();
              const data = textDecoder.decode(value);
              buffer += data;
              // console.log ('接收到原始数据:', data); // 打印原始数据
              // console.log ('当前缓冲区:', buffer); // 打印当前缓冲区内容

              // 查找完整的 JSON 对象
              let startIndex = -1;
              let endIndex = -1;
              let tempOpenBraces = 0;

              for (let i = 0; i < buffer.length; i++) {
                const char = buffer[i];
                if (char === '{') {
                  if (startIndex === -1) {
                    startIndex = i; // 找到第一个 '{'
                  }
                  tempOpenBraces++;
                } else if (char === '}') {
                  tempOpenBraces--;
                  if (tempOpenBraces === 0 && startIndex !== -1) {
                    endIndex = i; // 找到匹配的 '}'
                    break; // 找到一个完整的 JSON，退出循环
                  }
                }
              }

              if (startIndex !== -1 && endIndex !== -1 && tempOpenBraces === 0) {
                const jsonStr = buffer.substring(startIndex, endIndex + 1);
                // console.log('尝试解析的JSON字符串:', jsonStr);
                try {
                  const jsonData = JSON.parse(jsonStr);
                  console.log ('接收到并解析的串口数据:', jsonData);
                  commit('ADD_SERIAL_DATA', jsonData);

                  if (jsonData.id && state.pendingRequests[jsonData.id]) {
                    state.pendingRequests[jsonData.id].resolve(jsonData);
                    commit('REMOVE_PENDING_REQUEST', jsonData.id);
                  }
                  if (jsonData.method === "get_device_info_reply") {
                    commit('SET_DEVICE_INFO', jsonData);
                    Cookies.set('deviceInfo', JSON.stringify(jsonData));
                  }
                } catch (error) {
                  console.error('解析JSON错误:', error, 'JSON字符串:', jsonStr);
                }
                // 移除已处理的 JSON 字符串及之前的所有内容
                buffer = buffer.substring(endIndex + 1).trim();
                // 如果缓冲区中还有其他数据，继续处理
                if (buffer.length > 0) {
                    // console.log('处理后剩余缓冲区:', buffer);
                    // 重新开始查找下一个JSON对象
                    startIndex = -1;
                    endIndex = -1;
                    tempOpenBraces = 0;
                    // 由于可能有一个新的JSON对象紧随其后，我们不在此处跳出，而是让外层while循环继续
                }
              }
            }
          } catch (error) {
            // 确保在遇到断开连接错误时正确触发断开
            if (error.name === 'NetworkError' || error.name === 'AbortError' || error.name === 'DOMException') {
              console.error('串口设备已断开连接:', error);
              dispatch('disconnectSerial'); // 调用 action 来执行断开逻辑
              alert('串口设备已断开连接'); // 用户提示
              break; // 退出读取循环
            } else {
              console.error('读取串口数据失败:', error);
            }
          }
        }
        // 当循环结束（例如，断开连接）时，确保释放 reader 锁
        if (reader && !reader.locked) { // 检查是否仍持有锁，避免重复释放
            reader.releaseLock();
        }
      };
      readLoop();
    },
    sendSerialData({ commit, state }, data) {
      if (!state.serialPort || !state.serialPort.writable || !state.isSerialConnected) { // 检查 isSerialConnected
        console.error('串口未打开或不可写或未连接');
        return Promise.reject(new Error('串口未打开或不可写或未连接')); // 返回 rejected Promise
      }
      return new Promise((resolve, reject) => {
        if (data.id) {
          commit('ADD_PENDING_REQUEST', { id: data.id, resolve, reject });
        }
        const jsonString = JSON.stringify(data) + "\n"; // 确保以换行符结尾
        const encoder = new TextEncoder();
        const writer = state.writer;
        if (!writer) {
          console.error('Writer is not initialized.');
          reject(new Error('Writer is not initialized.'));
          return;
        }
        const encodedData = encoder.encode(jsonString);
        console.log('发送数据:', jsonString);
        writer.write(encodedData).catch(error => {
          console.error('发送数据失败:', error);
          reject(error);
        });
      });
    },
    disconnectSerial({ commit }) {
      commit('DISCONNECT_SERIAL');
    },

    // --- 新增蓝牙连接 Action ---
    // 这个 action 仅用于简化 LogNavComponent 中的逻辑，
    // 实际的 Bluetooth.requestDevice 和 connect 应该在组件中直接执行
    // 因为它们涉及到用户交互。
    // 但是，如果你希望在 action 中处理完整的连接流程，也可以这样做。
    // 这里我们保持之前LogNavComponent中的连接逻辑，并只使用Vuex来存储gattServer。
    // 所以，这里不需要一个 connectBle action，mutation 足够。
  },
  getters: {
    // --- 现有串口相关 Getters (修改 isConnected -> isSerialConnected) ---
    getSerialData: state => state.serialData,
    getIsSerialConnected: state => state.isSerialConnected, // 修改这里
    getSerialPort: state => state.serialPort,
    getWriter: state => state.writer,
    getReader: state => state.reader,
    getIsPortOpen: state => state.isPortOpen,
    getDeviceInfo: state => state.deviceInfo, // 获取串口设备信息

    // --- 新增蓝牙相关 Getters ---
    getGattServer: state => state.gattServer,
    getIsBleConnected: state => state.isBleConnected, // 蓝牙连接状态
    getBleDeviceInfo: state => state.bleDeviceInfo, // 获取蓝牙设备信息
  },
});